import { useEffect, useMemo } from "react";
import { getRegister, addSelectItem } from "../../utils/helpers";
import { URL_BILLING_INVOICES } from "../../utils/urls";
import { API_COLLECT_PAYMENT, API_INVOICES } from "../../utils/apis";
import { CASH, COUPON, MOBILE_MONEY } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import { useLocation } from "react-router-dom";
import useGet from "../common/useGet";

export const usePaymentsForm = ({
  id,
  form,
  navigate,
  updateFormValues,
}: TFormHookParams & { navigate?: any } = {}) => {
  const location = useLocation();
  const { phone_number, invoice } = form || {};
  const itemData = location.state?.item;
  const { data, loadData } = useGet();

  useEffect(() => {
    if (data) {
      updateFormValues({ amount: data.total_due });
    }
  }, [data]);

  const invoiceId = itemData?.consolidated_invoice || itemData.id;

  useEffect(() => {
    loadData({ api: `${API_INVOICES}/${invoiceId}` });
  }, [location.state.item]);

  // Use item data if available, otherwise fall back to form data
  const finalPhoneNumber = itemData?.phone_number || phone_number;
  const finalInvoice =
    itemData?.consolidated_invoice ||
    itemData?.id ||
    itemData?.invoice_number ||
    invoice;
  const finalAmount = data?.total_due;

  const couponPay = () => {
    if (navigate) {
      navigate(-1);
      const invoiceId = itemData?.id || finalInvoice;
      navigate(`/bills/invoices/${invoiceId}/details`);
    }
  };

  const fields: any = useMemo(
    () => [
      {
        field_name: "payment_mode",
        label: "Payment Mode",
        type: "select",
        props: { required: true },
        options_data: [
          addSelectItem({ label: "Mobile Money", value: MOBILE_MONEY }),
          addSelectItem({ label: "Cash", value: CASH }),
        ],
      },
      {
        field_name: "phone_number",
        label: "Phone Number",
        type: "tel",
        visible: form?.payment_mode === MOBILE_MONEY,
        props: {
          required: form?.payment_mode === MOBILE_MONEY,
          placeholder: "Mobile money number that will receive payment request",
        },
      },
      {
        field_name: "amount",
        label: "Amount",
        type: "number",
        props: {
          required: true,
          disabled: true,
          // value: finalAmount,
          placeholder: "Payment amount",
        },
      },
      {
        field_name: "invoice",
        label: "Invoice ID",
        type: "text",
        props: {
          disabled: true,
          value: finalInvoice,
          placeholder: "Invoice reference",
        },
      },
      // Add additional fields from item data if needed
      ...(itemData?.car_number
        ? [
            {
              field_name: "car_number",
              label: "Car Number",
              type: "text",
              props: {
                disabled: true,
                value: itemData.car_number,
                placeholder: "Vehicle registration",
              },
            },
          ]
        : []),
    ],
    [form?.payment_mode, finalAmount, finalInvoice, itemData],
  );

  const sections: IFormSection[] = useMemo(
    () => [
      {
        title: "Payment Information",
        fields: fields,
      },
    ],
    [fields],
  );

  const isEdit = !!id;
  const title = isEdit ? "Edit Payment" : "Initiate Payment";
  const redirect = getRegister(URL_BILLING_INVOICES);

  const defaultValues = {
    invoice: finalInvoice,
    phone_number: finalPhoneNumber,
    amount: finalAmount,
    ...(itemData?.car_number && { car_number: itemData.car_number }),
  };

  const onSubmit = (payload: any, currentId?: string) => {
    const transformedPayload = {
      ...payload,
      invoice_id: itemData?.id || payload.invoice,
      original_amount: itemData?.amount,
      balance: itemData?.balance,
    };

    // Handle coupon payment immediately
    if (payload.payment_mode === COUPON) {
      couponPay();
      return null;
    }

    if (currentId || id) {
      // Update existing payment
      return {
        id: currentId || id,
        api: API_COLLECT_PAYMENT,
        params: transformedPayload,
      };
    }

    // Create new payment
    return {
      api: API_COLLECT_PAYMENT,
      params: transformedPayload,
    };
  };

  return {
    fields,
    sections,
    title,
    redirect,
    defaultValues,
    loading: false,
    onSubmit,
    submitText: "Initiate Payment",
    itemData,
  };
};
